export interface HotspotSimpleInfo {
  id?: string;
  name?: string;
  description?: string;
  image?: {
    cover?: string;
  };
}

export interface HotspotFeedUser {
  avatar?: string;
  avatarUrl?: string;
  nickName?: string;
  nickname?: string;
  displayName?: string;
}

export interface HotspotFeedAttachment {
  type?: string;
  url?: string;
}

export interface HotspotFeed {
  id: string;
  cover?: string;
  summary?: string;
  creator?: HotspotFeedUser;
  user?: HotspotFeedUser;
  likeCount?: number;
  postShow?: {
    cover?: string;
    summary?: string;
    creator?: HotspotFeedUser;
    user?: HotspotFeedUser;
    likeCount?: number;
    attachments?: HotspotFeedAttachment[];
  };
  attachments?: HotspotFeedAttachment[];
}

export interface HotspotFeedListResponse {
  posts?: HotspotFeed[];
}

export interface HotspotShelfCardTitlePrefix {
  text?: string;
  color?: string;
  link?: string;
}

export interface HotspotShelfCardPrice {
  currency?: string;
  prefix?: string;
  value?: number | string;
  suffix?: string;
  valueV2?: string;
}

export interface HotspotShelfCardDisplay {
  cover?: string;
  title?: string;
  titlePrefix?: HotspotShelfCardTitlePrefix[];
  price?: HotspotShelfCardPrice;
  descriptions?: string[];
}

export interface HotspotShelfItem {
  id?: string;
  type?: string;
  cardDisplay?: HotspotShelfCardDisplay;
}

export interface HotspotShelfSection {
  title?: string;
  count?: number;
  list?: HotspotShelfItem[];
}

export interface HotspotGroupon {
  id?: string;
  title?: string;
  coverImages?: string[];
  eventStartedTime?: string;
  eventEndTime?: string;
}

export interface HotspotGrouponItem {
  groupon?: HotspotGroupon;
  shopInfo?: {
    shopId?: string;
    name?: string;
    avatar?: string;
  };
  seatInfo?: {
    hidenSeatsInfo?: boolean;
  };
  price?: {
    price?: string;
    urgentPrice?: string;
    value?: number;
    suffix?: string;
    Text?: string;
  };
}

export interface HotspotGrouponSection {
  title?: string;
  count?: number;
  list?: HotspotGrouponItem[];
}

export interface HotspotJumpLinkItem {
  title?: string;
  subTitle?: string;
  image?: string;
  jumpUrl?: string;
}

export interface HotspotJumpLinkSection {
  title?: string;
  count?: number;
  list?: HotspotJumpLinkItem[];
}

export interface HotspotAuctionFieldInfo {
  id?: string;
  fieldName?: string;
  // OPEN / PREPARE / CLOSE
  status?: string;
  // BLIND=暗拍
  auctionType?: string;
  // startTime 为毫秒，earliestAt / latestAt 为秒（mock 数据可能是秒），统一用 toMilliseconds 处理
  startTime?: string | number;
  earliestAt?: string | number;
  latestAt?: string | number;
  auctionCount?: string | number;
  bidsCount?: string | number;
  followCount?: string | number;
}

export interface HotspotAuctionLite {
  id?: string;
  cover?: string;
  medias?: HotspotFeedAttachment[];
  type?: string;
  status?: number;
  currentPrice?: number;
  startingPrice?: number;
  leadingPrice?: number;
}

export interface HotspotAuctionFieldItem {
  fieldInfo?: HotspotAuctionFieldInfo;
  auctionInfos?: HotspotAuctionLite[];
}

export interface HotspotAuctionFieldSection {
  title?: string;
  list: HotspotAuctionFieldItem[];
}

export interface HotspotSectionItem {
  type?: string;
  title?: string;
  shelfInfo?: HotspotShelfSection;
  grouponInfo?: HotspotGrouponSection;
  jumpLinkInfo?: HotspotJumpLinkSection;
  auctionFieldInfo?: {
    items?: HotspotAuctionFieldItem[];
    // 已废弃字段，服务端保留一轮兼容，只有拍场没有拍品
    fieldInfos?: HotspotAuctionFieldInfo[];
  };
}

export interface HotspotSectionResponse {
  hotspotId?: string;
  list?: HotspotSectionItem[];
  filterType?: string;
}

export interface HotspotFeedCard {
  id: string;
  cover?: string;
  summary?: string;
  ratio: number;
  creator?: HotspotFeedUser;
  likeCount?: number;
}

const HOTSPOT_SHELF_TYPE_LABELS: Readonly<Record<string, string>> = {
  BLIND_BOX_MACHINE: "抽盒机",
  B2C: "商城",
  MALL: "商城",
  LUCKY_BAG: "福袋",
  CROWD_FUNDING: "众筹",
  KUJI: "一番赏",
  NEW_LUCKY_BAG: "福袋",
  LOTTERY: "抽选",
  LARP: "剧本杀",
  VIP: "会员卡",
  PURCHASE: "采购",
  UNBOX: "拆盒",
  COMPOSE: "合成",
  LIVE_DRAW: "翻牌机",
  TICKET: "电子票",
  GACHA: "抽卡机",
  LIVE_GROUPON: "直播拼团",
};

export function getHotspotShelfTypeLabel(type?: string): string {
  if (!type) return "";
  return HOTSPOT_SHELF_TYPE_LABELS[type] ?? "";
}

export function getHotspotPriceValue(price?: HotspotShelfCardPrice): string {
  if (!price) return "";
  if (price.valueV2 !== undefined) return String(price.valueV2);
  if (price.value === undefined || price.value === null) return "";
  return String(price.value);
}

export function getHotspotCurrencySymbol(currency?: string): string {
  if (currency === "CNY") return "¥";
  if (currency === "USD") return "$";
  return currency ?? "";
}

export function hasHotspotPriceContent(price?: HotspotShelfCardPrice): boolean {
  return Boolean(
    price && (price.prefix || getHotspotPriceValue(price) || price.suffix)
  );
}

export function formatHotspotGrouponDate(eventStartedTime?: string): string {
  if (!eventStartedTime || Number.isNaN(Number(eventStartedTime))) return "";

  const parts = new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
  }).formatToParts(new Date(Number(eventStartedTime)));
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;
  const weekday = parts.find((part) => part.type === "weekday")?.value;

  if (!month || !day || !weekday) return "";

  return `${month}-${day} ${weekday}`;
}

export function selectHotspotGrouponSection(
  response?: HotspotSectionResponse | null
): HotspotGrouponSection | null {
  const section = response?.list?.find((item) => item?.type === "groupon");
  if (!section?.grouponInfo?.list?.length) return null;
  return section.grouponInfo;
}

export function selectHotspotShelfSection(
  response?: HotspotSectionResponse | null
): HotspotShelfSection | null {
  const section = response?.list?.find((item) => item?.type === "shelf");
  if (!section?.shelfInfo?.list?.length) return null;
  return section.shelfInfo;
}

export function selectHotspotJumpLinkSection(
  response?: HotspotSectionResponse | null
): HotspotJumpLinkSection | null {
  const section = response?.list?.find((item) => item?.type === "jump_link");
  if (!section?.jumpLinkInfo?.list?.length) return null;
  return section.jumpLinkInfo;
}

export type HotspotAuctionFieldPhase = "ongoing" | "preview" | "ended";

const AUCTION_FIELD_PHASE_ORDER: Readonly<
  Record<HotspotAuctionFieldPhase, number>
> = {
  ongoing: 0,
  preview: 1,
  ended: 2,
};

// 暗拍当前价服务端固定下发 999999，前端展示为 ???
const BLIND_PRICE_PLACEHOLDER = 999999;

export function toMilliseconds(value?: string | number): number {
  const num = Number(value);
  if (!num || Number.isNaN(num)) return 0;
  return num < 1e12 ? num * 1000 : num;
}

export function getAuctionFieldPhase(
  fieldInfo: HotspotAuctionFieldInfo | undefined,
  now: number
): HotspotAuctionFieldPhase {
  const startTime = toMilliseconds(fieldInfo?.startTime);
  const latestAt = toMilliseconds(fieldInfo?.latestAt);

  if (fieldInfo?.status === "CLOSE" || (latestAt && now >= latestAt)) {
    return "ended";
  }
  if (fieldInfo?.status === "PREPARE" || (startTime && now < startTime)) {
    return "preview";
  }
  return "ongoing";
}

export function selectHotspotAuctionFieldSection(
  response: HotspotSectionResponse | null | undefined,
  now: number
): HotspotAuctionFieldSection | null {
  const section = response?.list?.find(
    (item) => item?.type === "auction_field"
  );
  const info = section?.auctionFieldInfo;
  const items: HotspotAuctionFieldItem[] = info?.items?.length
    ? info.items
    : (info?.fieldInfos || []).map((fieldInfo) => ({ fieldInfo }));
  const list = items.filter((item) => item?.fieldInfo?.id);

  if (!list.length) return null;

  // 先按状态分组（进行中 → 预展 → 已结束），同一状态内保持服务端下发的运营顺序
  const sorted = list
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const diff =
        AUCTION_FIELD_PHASE_ORDER[getAuctionFieldPhase(a.item.fieldInfo, now)] -
        AUCTION_FIELD_PHASE_ORDER[getAuctionFieldPhase(b.item.fieldInfo, now)];
      return diff || a.index - b.index;
    })
    .map(({ item }) => item);

  return { title: section?.title, list: sorted };
}

export function getAuctionCover(auction: HotspotAuctionLite): string {
  return (
    auction?.cover ||
    auction?.medias?.find((item) => item?.type === "image")?.url ||
    ""
  );
}

function formatAuctionPrice(price?: number): string {
  if (price === undefined || price === null || Number.isNaN(Number(price))) {
    return "";
  }
  if (Number(price) === BLIND_PRICE_PLACEHOLDER) return "???";
  return String(Number(Number(price).toFixed(2)));
}

export function getAuctionPriceDisplay(
  auction: HotspotAuctionLite,
  fieldInfo: HotspotAuctionFieldInfo | undefined,
  phase: HotspotAuctionFieldPhase
): { label: string; value: string } {
  const isBlind =
    auction?.type === "BLIND" || fieldInfo?.auctionType === "BLIND";

  if (phase === "preview") {
    return {
      label: "起拍价",
      value: formatAuctionPrice(auction?.startingPrice),
    };
  }

  const price =
    auction?.currentPrice || auction?.leadingPrice || auction?.startingPrice;
  return {
    label: phase === "ended" ? "成交价" : "当前价",
    value: isBlind ? "???" : formatAuctionPrice(price),
  };
}

// 距截拍倒计时：未到首件截拍时间按首件算，逐件截拍进行中按最后一件算
export function getAuctionFieldCountdown(
  fieldInfo: HotspotAuctionFieldInfo | undefined,
  now: number
): { value: number; unit: string }[] {
  const earliestAt = toMilliseconds(fieldInfo?.earliestAt);
  const latestAt = toMilliseconds(fieldInfo?.latestAt);
  const target = earliestAt > now ? earliestAt : latestAt;
  const remain = Math.max(target - now, 0);

  const days = Math.floor(remain / 86400000);
  const hours = Math.floor((remain % 86400000) / 3600000);
  const minutes = Math.floor((remain % 3600000) / 60000);

  if (days > 0) {
    return [
      { value: days, unit: "天" },
      { value: hours, unit: "时" },
    ];
  }
  if (hours > 0) {
    return [
      { value: hours, unit: "时" },
      { value: minutes, unit: "分" },
    ];
  }
  return [{ value: Math.max(minutes, 1), unit: "分" }];
}

export function resolveHotspotFeedCover(feed: HotspotFeed): string | undefined {
  return (
    feed?.postShow?.cover ||
    feed?.cover ||
    feed?.postShow?.attachments?.find((item) => item?.type === "image")?.url ||
    feed?.attachments?.find((item) => item?.type === "image")?.url
  );
}

export function resolveHotspotFeedCreator(
  feed: HotspotFeed
): HotspotFeedUser | undefined {
  return (
    feed?.postShow?.creator ||
    feed?.creator ||
    feed?.postShow?.user ||
    feed?.user
  );
}

export function getHotspotFeedAspectRatio(feed: HotspotFeed): number {
  const cover = resolveHotspotFeedCover(feed);

  if (!cover) return 1;

  try {
    const url = new URL(cover);
    const imgInfo = url.searchParams.get("imginfo");

    if (!imgInfo) return 1;

    const [width, height] = imgInfo.split(",");
    const finalWidth = Number(width?.slice(1));
    const finalHeight = Number(height?.slice(1));

    if (!finalWidth || !finalHeight) return 1;

    return finalWidth / finalHeight;
  } catch {
    return 1;
  }
}

export function buildHotspotFeedCards(feeds: HotspotFeed[]): HotspotFeedCard[] {
  return (feeds || []).map<HotspotFeedCard>((feed) => ({
    id: feed.id,
    cover: resolveHotspotFeedCover(feed),
    summary: feed?.postShow?.summary || feed?.summary,
    ratio: getHotspotFeedAspectRatio(feed),
    creator: resolveHotspotFeedCreator(feed),
    likeCount: feed?.likeCount ?? feed?.postShow?.likeCount,
  }));
}
